import React, { useCallback, useMemo, useState } from 'react';

import { MdGroupAdd } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { DarkToolbarButton } from 'scouting-ui/lib/Button';
import { v4 as uuid } from 'uuid';

import { SBL_5198_CUB_BULK_ENTRY, SHOW_QUICK_ENTRY } from '@config/config';
import AdvancementImportModal from '@modules/advancement/advancementImport/components/AdvancementImportModal';
import { useCampImportStatus } from '@modules/advancement/advancementImport/hooks';
import { subUnitTypeSel } from '@modules/advancement/subUnits/duck/selectors';
import { isCubScoutsUnitSel } from '@modules/context/duck/selectorsTyped';
import QuickEntryDropdown from '@modules/roster-v2/components/QuickEntryDropdown';
import RosterImportButton from '@modules/roster-v2/components/RosterImportButton/RosterImportButton';
import DeleteSubUnitModal from '@modules/roster-v2/components/SubUnits/DeleteSubUnitModal/DeleteSubUnitModal';
import SubUnitModal from '@modules/roster-v2/components/SubUnits/SubUnitModal/SubUnitModal';
import { subUnitsEnabledSel } from '@modules/roster-v2/duck/selectors';
import {
  usePendingLevelChanges,
  useSubUnitPermissions,
} from '@modules/roster-v2/hooks/useSubUnitAdmin';
import { useSubUnitGroupsSource } from '@modules/roster-v2/hooks/useSubUnitGroupsSource';
import {
  buildSubUnitFilterOptions,
  collectSubUnitRefs,
} from '@modules/roster-v2/models/groupedRoster';
import type {
  RosterMemberView,
  SubUnitRef,
} from '@modules/roster-v2/models/rosterMemberView';
import { isInactiveUnitSel } from '@modules/unit/duck/selectorsTyped';
import { SBL_6013_importCouncilFileSel } from '@shared/duck/selectors/flags.selectors';
import {
  hasNewPermissionSel,
  isMobileSel,
} from '@shared/duck/selectors/general.selectors';
import { useUserPreference } from '@shared/hooks/useUserPreference';
import { intl } from '@shared/localization';
import { newPermissions } from '@shared/newPermissions';

import type { SubUnitActions } from './GroupHeader';
import RosterTableView from './RosterTableView';
import { type SubUnitLoadingState, getColumns } from './columns';

interface NewRosterContainerProps {
  orgGuid?: string;
  members: RosterMemberView[];
  loading?: boolean;
  subUnitLoading: SubUnitLoadingState;
}

const NO_MEMBERS: RosterMemberView[] = [];
const NO_SUB_UNIT_REFS: SubUnitRef[] = [];

/** One opening of a modal; a new `key` remounts it with fresh state. */
interface ModalSession<T> {
  key: string;
  open: boolean;
  data: T;
}

/**
 * `close()` keeps the modal mounted for its close animation; `clear()`, for
 * the Modal's `afterClose`, unmounts it unless a new session opened meanwhile.
 */
function useModalSession<T>() {
  const [session, setSession] = useState<ModalSession<T>>();

  const open = useCallback((data: T) => {
    setSession({ key: uuid(), open: true, data });
  }, []);
  const close = useCallback(
    () =>
      setSession(current =>
        current?.open ? { ...current, open: false } : current,
      ),
    [],
  );
  const clear = useCallback(
    () => setSession(current => (current?.open ? current : undefined)),
    [],
  );

  return { session, open, close, clear };
}

export default function NewRosterContainer(props: NewRosterContainerProps) {
  const { orgGuid, members, loading, subUnitLoading } = props;
  const [importOpen, setImportOpen] = useState(false);
  const isMobile = useSelector(isMobileSel);
  const { adults: adultSubUnitsLoading, youths: youthSubUnitsLoading } =
    subUnitLoading;

  const canImport = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.APPROVE_ADVANCEMENTS),
  );
  const showNewImport = useSelector(SBL_6013_importCouncilFileSel);
  const showImport = showNewImport && canImport;

  const campImport = useCampImportStatus({ enabled: showImport });

  const isInactiveUnit = useSelector(isInactiveUnitSel);
  const showQuickEntry =
    (SHOW_QUICK_ENTRY || SBL_5198_CUB_BULK_ENTRY) && !isInactiveUnit;

  const subUnitType = useSelector(subUnitTypeSel);
  const {
    canAdd: showAddSubUnit,
    canManage,
    canEdit,
  } = useSubUnitPermissions(orgGuid);

  // Grouping is for everyone who sees the roster, not only sub-unit editors
  const canGroup = useSelector(subUnitsEnabledSel);
  const [storedGrouped, setStoredGrouped] = useUserPreference(
    'rosterV2GroupBySubUnit',
    false,
  );
  const grouped = canGroup && storedGrouped;
  const { orgSubUnits, denLevelImages, settled } = useSubUnitGroupsSource({
    orgGuid,
    subUnitType,
    enabled: canGroup,
    grouped,
  });
  // Grouped, wait for the sub-units, or everyone shows under "No den/patrol"
  const waitingForGroups =
    grouped && (adultSubUnitsLoading || youthSubUnitsLoading || !settled);

  // One session per modal, since Delete opens on top of the editor
  const {
    session: editor,
    open: openEditor,
    close: closeEditor,
    clear: clearEditor,
  } = useModalSession<number | undefined>();
  const {
    session: deletion,
    open: openDeletion,
    close: closeDeletion,
    clear: clearDeletion,
  } = useModalSession<number>();
  const modalScope =
    canManage && orgGuid && subUnitType ? { orgGuid, subUnitType } : undefined;
  // Lives here, since the editor remounts on every opening
  const pendingLevelChanges = usePendingLevelChanges();
  // Close the editor too: the sub-unit is gone
  const handleDeleted = useCallback(() => {
    closeDeletion();
    closeEditor();
  }, [closeDeletion, closeEditor]);

  const subUnitActions = useMemo<SubUnitActions | undefined>(
    () =>
      canManage
        ? { canEdit, onEdit: openEditor, onDelete: openDeletion }
        : undefined,
    [canManage, canEdit, openEditor, openDeletion],
  );

  const positionFilters = useMemo(() => {
    const unique = Array.from(
      new Set(members.flatMap(m => m.positions.map(p => p.position))),
    ).sort((a, b) => a.localeCompare(b));
    return unique.map(p => ({ text: p, value: p }));
  }, [members]);

  const subUnitRefs = useMemo(
    () => collectSubUnitRefs(subUnitType, members, orgSubUnits),
    [subUnitType, members, orgSubUnits],
  );
  const subUnitFilters = useMemo(
    () =>
      buildSubUnitFilterOptions(
        subUnitRefs,
        subUnitType &&
          intl.formatMessage({
            id: `rosterV2.table.subUnitFilter.none.${subUnitType}`,
          }),
      ),
    [subUnitRefs, subUnitType],
  );

  const hasUnknownRegistration = useMemo(
    () => members.some(member => !member.registrationStatus),
    [members],
  );

  const isCubScout = useSelector(isCubScoutsUnitSel);
  const columns = useMemo(
    () =>
      getColumns({
        positionFilters,
        subUnitFilters,
        isCubScout,
        subUnitLoading: {
          adults: adultSubUnitsLoading,
          youths: youthSubUnitsLoading,
        },
        isMobile,
        hasUnknownRegistration,
        groupBy:
          canGroup && subUnitType
            ? { subUnitType, grouped, onChange: setStoredGrouped }
            : undefined,
      }),
    [
      positionFilters,
      subUnitFilters,
      isCubScout,
      adultSubUnitsLoading,
      youthSubUnitsLoading,
      isMobile,
      hasUnknownRegistration,
      canGroup,
      subUnitType,
      grouped,
      setStoredGrouped,
    ],
  );

  const toolbarExtra = (
    <>
      {showAddSubUnit && subUnitType && (
        <DarkToolbarButton
          icon={<MdGroupAdd />}
          onClick={() => openEditor(undefined)}
        >
          {intl.formatMessage({
            id: `rosterV2.table.addSubUnit.${subUnitType}`,
          })}
        </DarkToolbarButton>
      )}
      {showQuickEntry && orgGuid && <QuickEntryDropdown orgGuid={orgGuid} />}
      {showImport && (
        <RosterImportButton
          campImport={campImport}
          onClick={() => setImportOpen(true)}
        />
      )}
    </>
  );

  return (
    <>
      <RosterTableView
        // A new unit resets search, filters, sort, page and collapsed groups
        key={orgGuid}
        // No rows while waiting: antd only dims the rows under its spinner
        members={waitingForGroups ? NO_MEMBERS : members}
        loading={loading || waitingForGroups}
        columns={columns}
        subUnitType={subUnitType}
        grouped={grouped}
        subUnitRefs={waitingForGroups ? NO_SUB_UNIT_REFS : subUnitRefs}
        denLevelImages={denLevelImages}
        groupActions={subUnitActions}
        toolbarExtra={toolbarExtra}
      />
      {showImport && (
        <AdvancementImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
        />
      )}
      {modalScope && editor && (
        <SubUnitModal
          key={editor.key}
          open={editor.open}
          orgGuid={modalScope.orgGuid}
          subUnitType={modalScope.subUnitType}
          members={members}
          subUnitId={editor.data}
          pendingLevelChanges={pendingLevelChanges}
          onClose={closeEditor}
          afterClose={clearEditor}
          onDelete={openDeletion}
        />
      )}
      {modalScope && deletion && (
        <DeleteSubUnitModal
          key={deletion.key}
          open={deletion.open}
          orgGuid={modalScope.orgGuid}
          subUnitType={modalScope.subUnitType}
          subUnitId={deletion.data}
          members={members}
          onClose={closeDeletion}
          afterClose={clearDeletion}
          onDeleted={handleDeleted}
        />
      )}
    </>
  );
}
